import { useCallback, useEffect, useState } from "react";

import {
  FAMILY_PURCHASE_PATH,
  FAMILY_PURCHASE_RETURN_PATH,
} from "@shared/types/familyPortal";

/**
 * Minimal path-based router for the relatives portal. No dependency pulled in for
 * this: PORTAL-3 brings the route count to six flat paths and a `go`/`replace`
 * pair, which is still a lookup table with extra steps rather than a routing
 * problem. The CMS only justifies react-router because it has dozens of routes
 * AND nesting. Revisit when a screen needs a child route.
 *
 * Real navigation (not just client state) so a relative can bookmark the portal
 * and a hard refresh still lands on the right screen - `public/_redirects` sends
 * every path to index.html for that reason. It is also load-bearing for the buy
 * flow: the payment provider returns the browser to a URL, and that URL has to
 * resolve on a cold load with no client state at all.
 *
 * `/login/<token>` carries the single-use magic link, which is why matching is a
 * small function rather than a lookup table: the token is part of the path, not a
 * query parameter, so it never lands in a `Referer` header the way `?token=` would
 * when the page loads a third-party resource.
 */
export type Route =
  | { name: "home" }
  | { name: "login" }
  /** The magic link landed. `token` is spent the moment we consume it. */
  | { name: "login-token"; token: string }
  /** The purchase screen: price, what it buys, continue-to-payment. */
  | { name: "buy" }
  /** Back from the provider, possibly having paid. Proves nothing on its own. */
  | { name: "buy-return" }
  | { name: "not-found" };

function resolve(pathname: string): Route {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/") return { name: "home" };
  if (path === "/login") return { name: "login" };
  // The provider's return/cancel URLs are built server-side from these same
  // constants (services/familyCheckout.ts); a relative who paid us must not be
  // able to land on "Page not found" because two files drifted.
  if (path === FAMILY_PURCHASE_PATH) return { name: "buy" };
  if (path === FAMILY_PURCHASE_RETURN_PATH) return { name: "buy-return" };
  const magic = /^\/login\/(.+)$/.exec(path);
  if (magic) {
    return { name: "login-token", token: decodeURIComponent(magic[1]) };
  }
  return { name: "not-found" };
}

export interface Navigation {
  route: Route;
  /** Push a new path without a reload (History API), then re-resolve. */
  go(path: string): void;
  /**
   * REPLACE the current entry. Used the instant a magic link is consumed: the
   * spent token must leave the address bar and the back button, or the relative's
   * first instinct (share the link / press back) replays a URL that is now dead
   * and reads as a broken portal.
   */
  replace(path: string): void;
}

export function useNavigation(): Navigation {
  const [route, setRoute] = useState<Route>(() =>
    resolve(window.location.pathname)
  );

  useEffect(() => {
    const onPopState = () => setRoute(resolve(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const go = useCallback((path: string) => {
    window.history.pushState({}, "", path);
    setRoute(resolve(path));
  }, []);

  const replace = useCallback((path: string) => {
    window.history.replaceState({}, "", path);
    setRoute(resolve(path));
  }, []);

  return { route, go, replace };
}
